import { useEffect, useState } from 'react';
import Dialog, {
  DialogHeader,
  DialogHeading,
} from '@raydiant/raydial/components/Dialog';
import Modal from '@raydiant/raydial/components/Modal';
import Button from '@raydiant/raydial/components/Button';
import Text from '@raydiant/raydial/components/Text';
import Link from '@raydiant/raydial/components/Link';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined';
import cn from 'classnames';

// "Just copy them" maps to copy_out_of_tree_unowned: true (own copies of the
// unowned files), "Link them to the original" maps to false (stay linked to the
// originals). See useCopyContent for the request flow.
const HELP_ARTICLE_URL =
  'https://help.displai.ai/digital-signage/5imknKqrcgd6fwhKk5jfp3/duplicating-folders-playlists-and-presentations/7Yeoa51JWCmP7225Eyeezr';

interface CopyUnownedContentDialogProps {
  isOpen: boolean;
  isLoading?: boolean;
  onCancel: () => void;
  onConfirm: (copyOutOfTreeUnowned: boolean) => void;
}

const OPTIONS: {
  value: boolean;
  title: string;
  description: string;
  icon: JSX.Element;
}[] = [
  {
    value: true,
    title: 'Just copy them',
    description:
      'You’ll get your own versions of the files, stashed in a folder called "Supporting Content". You can edit those files freely, and if the owner edits the original, you will not get the edits.',
    icon: <FileCopyOutlinedIcon />,
  },
  {
    value: false,
    title: 'Link them to the original',
    description:
      'We’ll duplicate what’s yours, but for everything else you’ll stay linked to the original versions of the files. If the original owner makes changes, they will show up in your duplicate.',
    icon: <LinkOutlinedIcon />,
  },
];

const CopyUnownedContentDialog = ({
  isOpen,
  isLoading = false,
  onCancel,
  onConfirm,
}: CopyUnownedContentDialogProps) => {
  // Default to "Just copy them", matching the design.
  const [selected, setSelected] = useState(true);

  // Reset the selection whenever the dialog is reopened so a previous run's
  // choice doesn't leak into the next one.
  useEffect(() => {
    if (isOpen) setSelected(true);
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen}>
      <Dialog showCloseButton={true} onClose={onCancel}>
        <DialogHeader>
          <DialogHeading>
            <span className="text-black">
              One question about your duplicate…
            </span>
          </DialogHeading>
        </DialogHeader>

        <div className="px-4 py-6">
          <Text className="text-black" variant="body">
            We're making your copy, but some things you want to duplicate are
            owned by other people and you can't edit them. Choose how to handle
            them:
          </Text>

          <div
            role="radiogroup"
            aria-label="How to handle content owned by other people"
            className="mt-6 flex flex-col gap-3"
          >
            {OPTIONS.map((option) => {
              const isSelected = selected === option.value;
              return (
                <button
                  key={option.title}
                  type="button"
                  role="radio"
                  onClick={() => setSelected(option.value)}
                  aria-checked={isSelected}
                  className={cn(
                    'flex items-start gap-3 rounded-xl border p-4 text-left transition-colors',
                    isSelected
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full border-2',
                      isSelected ? 'border-blue-600' : 'border-gray-300',
                    )}
                  >
                    {isSelected && (
                      <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                    )}
                  </span>
                  <span className="flex-none text-black">{option.icon}</span>
                  <span className="flex flex-col gap-1">
                    <span className="text-lg font-bold text-black">
                      {option.title}
                    </span>
                    <span className="text-base text-gray-500">
                      {option.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-4 text-right">
            <Text variant="body" className="text-black">
              <span className="font-bold">Not sure what to do? </span>
              <Link
                href={HELP_ARTICLE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Read this help article.
              </Link>
            </Text>
          </div>
        </div>

        <div className="flex flex-col-reverse justify-between pt-2 pb-4 gap-3 px-0 md:gap-6 md:flex-row md:px-4">
          <Button
            onPress={() => onCancel()}
            variant="secondary"
            size="large"
            className="grow"
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onPress={() => onConfirm(selected)}
            size="large"
            className="grow"
            isDisabled={isLoading}
            loading={isLoading}
          >
            Duplicate
          </Button>
        </div>
      </Dialog>
    </Modal>
  );
};

export default CopyUnownedContentDialog;
